import { type Dispatch, useReducer } from "react";

import { produce } from "immer";

import { HttpError } from "@/stf.data/client.panich";
import {
  type Cart,
} from "@/stf.data/schema";

type SelectedCartItemId = string[];
type TotalDetail = {
  stickerPrice: number;
  finalPrice: number;
  itemCount: number;
};

interface CartState {
  cartData: Cart;
  errors?: null | string;
  isSubmitting?: boolean;
  selectedCartItemId: SelectedCartItemId;
  totalDetail: TotalDetail;
}

type CartAction =
  {
    payload?: never;
    type: "toggleAll";
  } | {
    type: "clearError";
    payload?: never;
  } | {
    type: "initData";
    payload: {
      cartData: Cart;
      selectedCartItemId: SelectedCartItemId;
    };
  } | {
    type: "setError";
    payload: {
      error: unknown;
      defaultMsg: string;
    };
  } | {
    type: "setIsSubmitting";
    payload: boolean;
  } | {
    type: "toggleItem";
    payload: string;
  } | {
    type: "toggleShop";
    payload: string;
  };

const calTotalDetail = (state: CartState): TotalDetail => {
  const t: TotalDetail = {
    stickerPrice: 0,
    finalPrice: 0,
    itemCount: 0,
  };

  const {
    cartData: {
      itemList: cartItemList,
    },
    selectedCartItemId,
  } = state;

  cartItemList.forEach((cartItem) => {
    if (selectedCartItemId.includes(cartItem.cartItemId)) {
      const {
        selectedVariant: { retailPrice, promoPrice },
        qty,
      } = cartItem;

      t.itemCount += 1;
      t.stickerPrice += retailPrice * qty;
      t.finalPrice += promoPrice * qty;
    }
  });

  return t;
};

interface ReduceShopStateProps {
  cartData: Cart;
  selectedCartItemId: SelectedCartItemId;
  shopSlug: string;
}
const reduceShopState = (props: ReduceShopStateProps): boolean => {
  const { cartData, selectedCartItemId, shopSlug } = props;

  return cartData.itemList.reduce((acc, cartItem) => {
    const { shopDetail: { shopSlug: itemShopSlug } } = cartItem;

    if (selectedCartItemId.length === 0) {
      return false;
    }

    if ((itemShopSlug === shopSlug)
      && (cartItem.selectedVariant.stock > 0)
    ) {
      const shopItemState = selectedCartItemId.includes(cartItem.cartItemId);
      return acc && shopItemState;
    }
    return acc;
  }, true);
};

interface ReduceAllStateProps {
  cartData: Cart;
  selectedCartItemId: SelectedCartItemId;
}
const reduceAllState = (props: ReduceAllStateProps): boolean => {
  const { cartData, selectedCartItemId } = props;

  if (selectedCartItemId.length === 0) {
    return false;
  }

  const avaliableItemList = cartData.itemList.filter(
    (cartItem) => { return cartItem.selectedVariant.stock > 0; },
  );

  if (selectedCartItemId.length < avaliableItemList.length) {
    return false;
  }

  return true;
};

const cartReducer = produce((
  s: CartState,
  action: CartAction,
) => {
  switch (action.type) {
    case "initData": {
      const { payload: { cartData, selectedCartItemId } } = action;

      s.cartData = cartData;
      s.selectedCartItemId = selectedCartItemId;

      break;
    }
    case "toggleAll": {
      if (s.selectedCartItemId.length === 0) {
        s.selectedCartItemId = s.cartData.itemList.reduce<SelectedCartItemId>((acc, cartItem) => {
          if (cartItem.selectedVariant.stock > 0) {
            acc.push(cartItem.cartItemId);
          }
          return acc;
        }, []);
      }
      else {
        s.selectedCartItemId = [];
      }

      break;
    }
    case "toggleItem": {
      const { payload: cartItemId } = action;

      if (s.selectedCartItemId.includes(cartItemId)) {
        s.selectedCartItemId = s.selectedCartItemId.filter(
          (id) => { return id !== cartItemId; },
        );
      }
      else {
        s.selectedCartItemId.push(cartItemId);
      }

      break;
    }
    case "toggleShop": {
      const { payload: shopSlug } = action;

      const currentShopState = reduceShopState({
        cartData: s.cartData,
        selectedCartItemId: s.selectedCartItemId,
        shopSlug,
      });

      const selectedCartItem = s.cartData.itemList.filter((cartItem) => {
        const { shopDetail: { shopSlug: itemShopSlug } } = cartItem;

        if (
          (itemShopSlug === shopSlug)
          && (cartItem.selectedVariant.stock > 0)
        ) {
          return !currentShopState;
        }
        return s.selectedCartItemId.includes(cartItem.cartItemId);
      });
      s.selectedCartItemId = selectedCartItem.map((item) => {
        return item.cartItemId;
      });

      break;
    }

    case "setError":
      return produce(s, (draft) => {
        const e = action.payload.error;
        if (e instanceof HttpError) {
          draft.errors = e.message;
        }
        else {
          draft.errors = action.payload.defaultMsg;
        }
      });

    case "clearError":
      return produce(s, (draft) => {
        draft.errors = null;
      });

    case "setIsSubmitting":
      return produce(s, (draft) => {
        draft.isSubmitting = action.payload;
      });

    default: {
      throw new Error("Invalid CartReducer action");
    }
  }

  s.totalDetail = calTotalDetail(s);
});

const blankCartState: CartState = {
  cartData: {
    itemList: [],
    shopList: [],
  },
  selectedCartItemId: [],
  totalDetail: {
    stickerPrice: 0,
    finalPrice: 0,
    itemCount: 0,
  },
};

const useCart = (): [
  CartState,
  Dispatch<CartAction>,
] => {
  return useReducer(cartReducer, blankCartState);
};

export {
  type CartAction,
  type CartState,
  blankCartState,
  cartReducer,
  reduceAllState,
  reduceShopState,
  useCart,
};
